import { Camera } from "src/SharedGL/Camera";
import { FullDayBuffer } from "./FullDayBuffer";
import { TextureShader } from "./shaders/TextureShader";
import { PairingBuffer } from "./shaders/PairingBuffer";
import { PairingShader } from "./shaders/PairingShader";
export type DayMode = 0 | 1 | 2 | 4 | 3 | 5 | 6 | 7; // bit flags: 1=R, 2=Y, 4=O; combos use OR

export class PerDayViolationOverlayGL {
  private full: FullDayBuffer;
  private flat: PairingBuffer;

  constructor(
    gl: WebGLRenderingContext,
    textureShader: TextureShader,
    texture: WebGLTexture,
    flatShader: PairingShader
  ) {
    this.full = new FullDayBuffer(gl, textureShader, texture);
    this.flat = new PairingBuffer(gl, flatShader);
  }

  drawDay(
    gl: WebGLRenderingContext,
    camera: Camera,
    yTop: number,
    yBottom: number,
    dayStartMs: number,
    dayEndMs: number,
    z: number,
    mode: DayMode,
    overlayAlpha = 0.18,
    tint = 1.0,
    bgFloor?: number
  ) {
    if (mode === 0) return;

    const x0 = dayStartMs - Camera.referenceTime;
    const x1 = dayEndMs - Camera.referenceTime;

    const positions = [x0, yTop, x1, yTop, x1, yBottom, x0, yTop, x1, yBottom, x0, yBottom];
    const zeros = [0, 0, 0, 0];
    const colors = [...zeros, ...zeros, ...zeros, ...zeros, ...zeros, ...zeros];
    this.full.updateBuffers(gl, positions, colors);

    const [minX, maxX] = camera.getXRange();
    const xScale = gl.canvas.width / (maxX - Camera.referenceTime - (minX - Camera.referenceTime));
    const scale = [xScale, 0.01666];

    const yMid = 0.5 * (yTop + yBottom);
    const yMidV = yMid * scale[1];
    const frac = yMidV - Math.floor(yMidV); // fract()
    const patternOffsetY = 0.5 - frac;

    const wasBlend = gl.isEnabled(gl.BLEND);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    this.full.draw(gl, camera, 0, 0, 6, scale, z, {
      mode,
      overlayAlpha,
      tint,
      bandX0: x0,
      bandX1: x1,
      bgFloor: 0.6,
      patternOffsetY,
      blendWidth: 0.2,
      splitMid: 0.5, // exact middle for 2-color
      splitL: 1.0 / 3.0, // thirds for 3-color
      splitR: 2.0 / 3.0,
    });

    if (!wasBlend) gl.disable(gl.BLEND);
  }

  drawTopStripCombined(
    gl: WebGLRenderingContext,
    camera: Camera,
    yStart: number,
    height: number,
    dayStartMs: number,
    dayEndMs: number,
    mode: DayMode
  ) {
    if (mode === 0) return;

    const x0 = dayStartMs - Camera.referenceTime;
    const x1 = dayEndMs - Camera.referenceTime;

    const R: [number, number, number, number] = [1, 0, 0, 1];
    const Y: [number, number, number, number] = [1, 1, 0, 1];
    const O: [number, number, number, number] = [0.75, 0.5, 0, 1];

    type Seg = { x0: number; x1: number; c: [number, number, number, number] };
    const segs: Seg[] = [];

    if (mode === 7) {
      // R|Y|O
      const w = (x1 - x0) / 3;
      segs.push({ x0, x1: x0 + w, c: R });
      segs.push({ x0: x0 + w, x1: x0 + 2 * w, c: Y });
      segs.push({ x0: x0 + 2 * w, x1, c: O });
    } else if (mode === 3) {
      // R|Y
      const m = 0.5 * (x0 + x1);
      segs.push({ x0, x1: m, c: R });
      segs.push({ x0: m, x1, c: Y });
    } else if (mode === 5) {
      // R|O
      const m = 0.5 * (x0 + x1);
      segs.push({ x0, x1: m, c: R });
      segs.push({ x0: m, x1, c: O });
    } else if (mode === 6) {
      // Y|O
      const m = 0.5 * (x0 + x1);
      segs.push({ x0, x1: m, c: Y });
      segs.push({ x0: m, x1, c: O });
    } else {
      const c = mode === 1 ? R : mode === 2 ? Y : O;
      segs.push({ x0, x1, c });
    }

    // local strip coords (like legacy)
    const y0 = 0,
      y1 = height;
    const positions: number[] = [];
    const colors: number[] = [];

    for (const s of segs) {
      positions.push(s.x0, y0, s.x1, y0, s.x1, y1, s.x0, y0, s.x1, y1, s.x0, y1);
      for (let i = 0; i < 6; i++) colors.push(...s.c);
    }

    this.flat.updateBuffers(gl, positions, colors);

    // guard state so nothing clips it
    const wasBlend = gl.isEnabled(gl.BLEND);
    const wasDepth = gl.isEnabled(gl.DEPTH_TEST);
    const wasScissor = gl.isEnabled(gl.SCISSOR_TEST);
    const wasStencil = gl.isEnabled(gl.STENCIL_TEST);
    const depthWrite = gl.getParameter(gl.DEPTH_WRITEMASK) as boolean;

    if (!wasBlend) gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    if (wasDepth) gl.disable(gl.DEPTH_TEST);
    if (wasScissor) gl.disable(gl.SCISSOR_TEST);
    if (wasStencil) gl.disable(gl.STENCIL_TEST);
    gl.colorMask(true, true, true, true);
    gl.depthMask(false);

    const numVerts = positions.length / 2;
    this.flat.draw(gl, camera, /* yPos */ yStart, /* z */ -1.0, /* offset */ 0, numVerts);

    // restore
    gl.depthMask(depthWrite);
    if (wasStencil) gl.enable(gl.STENCIL_TEST);
    if (wasScissor) gl.enable(gl.SCISSOR_TEST);
    if (wasDepth) gl.enable(gl.DEPTH_TEST);
    if (!wasBlend) gl.disable(gl.BLEND);
  }
}

export const DayBits = {
  None: 0,
  Red: 1,
  Yellow: 2,
  Orange: 4,
} as const;

export function makeDayMode(red: boolean, yellow: boolean, orange: boolean): DayMode {
  let m = DayBits.None;
  if (red) m |= DayBits.Red;
  if (yellow) m |= DayBits.Yellow;
  if (orange) m |= DayBits.Orange;
  return m as DayMode;
}
